/**
 * Word-wall archetype — the Ink Factory graphic-recording register.
 *
 * The page is text: a giant title word anchors the sheet, and tree-shaped
 * clusters (headline → sub-heads on highlighter boxes → fragments) scatter
 * around it, each with a spot icon from the symbol library beside its
 * headline. No backdrop. Three inks: ink, accent, tint.
 *
 * Spec shape (see fixtures/growth-wall.json):
 *   {
 *     archetype: 'wall',
 *     title: { qualifier: 'STRATEGIC', word: 'GROWTH' },
 *     fonts: { caps: 'Patrick Hand', script: 'Kalam' },
 *     palette: { ink, accent, tint, paper },
 *     clusters: [{
 *       id, headline, icon_symbol_id, near?, sent?,
 *       children: [{ subhead, sent?, fragments: [{ text, sent? } | string] }],
 *       quotes: [{ text, sent? }]          // sparse full sentences, script face
 *     }]
 *   }
 *
 * Registers → size (viewBox units, sheet = 1000 tall), face, ink:
 *   title word 170 caps accent · qualifier 54 caps ink · headline 52 caps accent
 *   · subhead 30 caps ink on tint box · fragment 22 caps ink · quote 24 script ink
 */

import { LINE_GAP, up, wrap, emitClusterItems, latestSent, placeTitle, iconSymbolId } from './sceneText.js';

const CLUSTER_MAX_W = 640;

export const WALL = {
  titleWord: 150,
  qualifier: 48,
  headline: 42,
  subhead: 25,
  fragment: 19,
  quote: 21,
  lineGap: LINE_GAP,
  boxPad: 5,
  iconSize: 64,
  iconGap: 10,
  childGap: 16, // between child blocks in a row
  rowGap: 10,
  fragIndent: 24, // room for the hook arrow's horizontal leg
  fragMaxW: 220,
  clusterMaxW: CLUSTER_MAX_W,
  clusterPad: 8,
  // Quotes wrap at a fixed width (capped by the attempt's maxW), never at the
  // tree's current width: a child added later must not re-wrap a quote that
  // is already on the wall.
  quoteMaxW: 480,
  clusterGap: 10, // pad between a cluster's parts and anything else
  arrowStroke: 2.4, // fragment hook arrows
  arrowHead: 7,
  // Title: the word never takes more than 60% of the sheet width; the
  // reserve covers its cap height (~0.82 of the size), not the full line box.
  titleY: 26,
  titleMaxFrac: 0.6,
  titleReserve: { padL: 20, padR: 20, top: 20, capFrac: 0.82, below: 0 },
  // Fit ladder: full → narrow → shrunk. Only the first `ladderNear` rungs
  // run near the target: a cluster moves elsewhere at full size before it
  // shrinks past legibility; the whole ladder is the last resort anywhere.
  ladder: [
    { maxW: CLUSTER_MAX_W, scale: 1 },
    { maxW: CLUSTER_MAX_W * 0.68, scale: 1 },
    { maxW: CLUSTER_MAX_W * 0.68, scale: 0.85 },
    { maxW: CLUSTER_MAX_W * 0.68, scale: 0.72 },
    { maxW: CLUSTER_MAX_W * 0.55, scale: 0.62 },
  ],
  ladderNear: 3,
};

/**
 * Lay out one cluster in local coords (origin top-left). Returns
 * { w, h, items } where items are { kind, text, x, y, w, h, fontSize, lines, widths, ... }.
 */
export function layoutCluster(cl, measure, fonts, { maxW = WALL.clusterMaxW, scale = 1, quoteFs = null } = {}) {
  // `scale` shrinks every register (last-resort fit); `maxW` narrows the row
  // flow so a cluster can go tall instead of wide; `quoteFs` sets the
  // quotes' size outright (a story keeps its own script register whatever
  // the tree's scale — TT-1162 note 3). What of the cluster is laid out is
  // the caller's: a fit step that writes less hands in a trimmed cluster.
  const S = (v) => v * scale;
  const capsM = (t, fs) => measure(up(t), fs, fonts.caps);
  const scriptM = (t, fs) => measure(t, fs, fonts.script);
  const items = [];
  let x = 0;
  let y = 0;

  // Icon + headline row.
  const symbolId = iconSymbolId(cl);
  const hasIcon = !!symbolId;
  const iconSize = S(WALL.iconSize);
  const iconW = hasIcon ? iconSize + WALL.iconGap : 0;
  const head = wrap(up(cl.headline), S(WALL.headline), maxW - iconW, capsM);
  const headH = Math.max(head.h, hasIcon ? iconSize : 0);
  if (hasIcon) items.push({ kind: 'icon', symbolId, x: 0, y: (headH - iconSize) / 2, w: iconSize, h: iconSize });
  items.push({ kind: 'headline', text: up(cl.headline), x: iconW, y: (headH - head.h) / 2, ...head, fontSize: S(WALL.headline) });
  let width = iconW + head.w;
  y = headH + WALL.rowGap * 0.6;

  // Children flow left→right, wrapping at clusterMaxW.
  const children = cl.children || [];
  let rowX = 0;
  let rowH = 0;
  let rowStartY = y;
  for (const ch of children) {
    // Measure child block.
    const sub = wrap(up(ch.subhead), S(WALL.subhead), S(WALL.fragMaxW) + 40, capsM);
    const subBox = { w: sub.w + WALL.boxPad * 2, h: sub.h + WALL.boxPad * 1.2 };
    const frags = (ch.fragments || []).map((f) => (typeof f === 'string' ? { text: f } : f)).filter((f) => f && f.text);
    const fragLayouts = frags.map((f) => ({ ...f, ...wrap(up(f.text), S(WALL.fragment), S(WALL.fragMaxW), capsM) }));
    const fragH = fragLayouts.reduce((a, f) => a + f.h + 4, 0);
    const fragW = Math.max(0, ...fragLayouts.map((f) => f.w)) + WALL.fragIndent;
    const bw = Math.max(subBox.w, fragW);
    const bh = subBox.h + (fragLayouts.length ? 8 + fragH : 0);
    if (rowX > 0 && rowX + bw > maxW) {
      rowX = 0;
      rowStartY += rowH + WALL.rowGap;
      rowH = 0;
    }
    const bx = rowX;
    const by = rowStartY;
    items.push({ kind: 'box', key: up(ch.subhead), x: bx, y: by, w: subBox.w, h: subBox.h, sent: ch.sent });
    items.push({ kind: 'subhead', text: up(ch.subhead), x: bx + WALL.boxPad, y: by + WALL.boxPad * 0.5, ...sub, fontSize: S(WALL.subhead), sent: ch.sent });
    let fy = by + subBox.h + 8;
    fragLayouts.forEach((f, i) => {
      items.push({ kind: 'fragment', text: up(f.text), x: bx + WALL.fragIndent, y: fy, w: f.w, h: f.h, lines: f.lines, widths: f.widths, fontSize: S(WALL.fragment), sent: f.sent ?? ch.sent });
      if (i === 0) {
        // Short hook arrow from the box's bottom-left down to the first fragment.
        items.push({ kind: 'arrow', key: up(f.text), x: bx + 4, y: by + subBox.h, x2: bx + WALL.fragIndent - 4, y2: fy + f.h * 0.5, sent: f.sent ?? ch.sent });
      }
      fy += f.h + 4;
    });
    rowX += bw + WALL.childGap;
    rowH = Math.max(rowH, bh);
    width = Math.max(width, bx + bw);
  }
  y = children.length ? rowStartY + rowH : y;

  // Quotes: full-width script lines under the tree.
  const qfs = quoteFs || S(WALL.quote);
  for (const q of cl.quotes || []) {
    if (!q || !q.text) continue;
    y += WALL.rowGap;
    const text = `“${q.text}”`;
    const qw = wrap(text, qfs, Math.min(maxW, WALL.quoteMaxW), scriptM);
    items.push({ kind: 'quote', text, key: q.text, x: 0, y, ...qw, fontSize: qfs, sent: q.sent });
    width = Math.max(width, qw.w);
    y += qw.h;
  }
  return { w: width + WALL.clusterPad * 2, h: y + WALL.clusterPad * 2, items };
}

/**
 * A cluster's footprint for occupancy: its visible parts, offset by the
 * cluster pad, so neighbours may tuck into its whitespace.
 */
export function clusterParts(local) {
  return local.items.filter((it) => it.kind !== 'arrow').map((it) => ({ x: it.x + WALL.clusterPad, y: it.y + WALL.clusterPad, w: it.w, h: it.h }));
}

// Where clusters go when the spec gives no `near`: a ring around the title.
const RING = [
  [0.14, 0.58],
  [0.86, 0.58],
  [0.5, 0.66],
  [0.14, 0.2],
  [0.86, 0.2],
  [0.3, 0.88],
  [0.7, 0.88],
];

/**
 * @param spec   wall spec
 * @param ctx    { sheet, occ, zones, rects, placed, skipped }
 * @param helpers { measure(text, fs, family), placeBlock, commitSpot, holdLadder, heldFirst, resolveNear, resolveZone, palette, fonts, prev }
 * @returns { texts, shapes, icons, focus }
 */
export function solveWall(spec, ctx, { measure, placeBlock, commitSpot, holdLadder, heldFirst, resolveNear, resolveZone, palette, fonts, prev }) {
  const { sheet } = ctx;
  const texts = [];
  const shapes = [];
  const icons = [];
  const capsM = (t, fs) => measure(t, fs, fonts.caps);
  const E = { texts, shapes, icons };

  // ---- Title: qualifier over the giant word, centered, top. A two-word
  // title ("BUSINESS PARTNERING") at full size would span the sheet; it is
  // scaled to WALL.titleMaxFrac.
  placeTitle(spec, ctx, {
    measure: capsM,
    E,
    commitSpot,
    fonts,
    palette,
    sizes: { word: WALL.titleWord, qualifier: WALL.qualifier },
    maxFrac: WALL.titleMaxFrac,
    center: true,
    x: sheet.w / 2,
    y: WALL.titleY,
    reserve: WALL.titleReserve,
  });

  // ---- Clusters.
  const clusters = spec.clusters || [];
  // Clusters already on the wall (a `prev` rect) go first so a new cluster
  // can never take the spot a held one is about to reclaim; `ci` (the ring
  // slot) stays the spec index so a newcomer's default target does not
  // depend on how many neighbours are held.
  heldFirst(clusters.map((cl, ci) => ({ cl, ci })), prev, (it) => it.cl.id).forEach(({ cl, ci }) => {
    const attempts = WALL.ladder;
    let local = null;
    let r = null;
    const zone = resolveZone(cl.zone, ctx);
    let target = resolveNear(cl.near, ctx);
    if (!target) {
      const [fx, fy] = RING[ci % RING.length];
      target = [fx * sheet.w, fy * sheet.h];
    }
    const layoutAt = (opt) => {
      const L = layoutCluster(cl, measure, fonts, opt);
      L.parts = clusterParts(L);
      return L;
    };
    const tryAt = (z, t) => (L, holdOnly = false) => {
      const found = placeBlock(ctx, prev, { id: cl.id, w: L.w, h: L.h, zone: z, target: t, pad: WALL.clusterGap, parts: L.parts, holdOnly });
      if (found) local = L;
      return found;
    };
    const tryAttempts = (z, t, opts) => {
      const at = tryAt(z, t);
      for (const opt of opts) {
        const found = at(layoutAt(opt));
        if (found) return found;
      }
      return null;
    };
    // A held cluster (previous solve) first asks whether its old spot can
    // absorb ANY layout on the ladder (same size verbatim, else grown in
    // place) before any layout is allowed to move it.
    if (prev?.[cl.id]) r = holdLadder(prev[cl.id], attempts.map(layoutAt), tryAt(zone, target))?.r || null;
    // Otherwise: near the target at full size → narrowed; then anywhere on
    // the sheet down the whole ladder (a smaller register is the last resort).
    if (!r) r = tryAttempts(zone, target, attempts.slice(0, WALL.ladderNear));
    if (!r) r = tryAttempts(ctx.zones.anywhere, [sheet.w / 2, sheet.h / 2], attempts);
    if (!r) return;
    emitClusterItems(cl, local, r, { E, palette, fonts, clusterPad: WALL.clusterPad, arrowStroke: WALL.arrowStroke, arrowHead: WALL.arrowHead });
  });

  // Camera focus: the placed cluster that arrived last (largest `sent`).
  let focus = null;
  for (const cl of clusters) {
    const r = ctx.rects[cl.id];
    if (!r) continue;
    const latest = latestSent(cl);
    if (!focus || latest >= focus.sent) focus = { id: cl.id, sent: latest, rect: r };
  }
  return { texts, shapes, icons, focus };
}

/** Wall zones need no anchor: sheet thirds + top/bottom bands. */
export function wallZones(sheet, margin) {
  const third = sheet.w * 0.34;
  const rect = (x, y, w, h) => [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
  return {
    anywhere: rect(margin, margin, sheet.w - 2 * margin, sheet.h - 2 * margin),
    left: rect(margin, margin, third, sheet.h - 2 * margin),
    right: rect(sheet.w - third, margin, third - margin, sheet.h - 2 * margin),
    top: rect(margin, margin, sheet.w - 2 * margin, sheet.h * 0.4),
    bottom: rect(margin, sheet.h * 0.55, sheet.w - 2 * margin, sheet.h * 0.45 - margin),
    sky: rect(margin, margin, sheet.w - 2 * margin, sheet.h * 0.45),
    ground: rect(margin, sheet.h * 0.55, sheet.w - 2 * margin, sheet.h * 0.45 - margin),
  };
}
